import { FaIcon, faPencilSolid, faPlusSolid, faSave } from '@/lib/fa'
import { faCancel, faTimes } from '@fortawesome/free-solid-svg-icons'
import React, { useMemo, useState } from 'react'
import clx from 'classnames'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type CsvPillsInputProps = {
  value: string
  setValue: (next: string) => void
  separator?: string
  placeholder?: string
  className?: string
  trimValues?: boolean
  allowEmpty?: boolean
}

function parseCsv(
  input: string,
  separator: string,
  trimValues: boolean,
  allowEmpty: boolean,
): string[] {
  const parts = input.split(separator)

  const normalized = parts.map((item) => (trimValues ? item.trim() : item))

  return allowEmpty ? normalized : normalized.filter((item) => item !== '')
}

function stringifyCsv(items: string[], separator: string): string {
  return items.join(`${separator} `)
}

export function CsvPillsInput({
  value,
  setValue,
  separator = ',',
  placeholder = 'Dodaj...',
  className,
  trimValues = true,
  allowEmpty = false,
}: CsvPillsInputProps) {
  const { tSafe } = useLocalize()
  const items = useMemo(
    () => parseCsv(value, separator, trimValues, allowEmpty),
    [value, separator, trimValues, allowEmpty],
  )

  const [draft, setDraft] = useState('')
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingValue, setEditingValue] = useState('')

  const updateItems = (nextItems: string[]) => {
    const normalized = allowEmpty
      ? nextItems
      : nextItems.filter((item) => item.trim() !== '')

    setValue(stringifyCsv(normalized, separator))
  }

  const startEdit = (index: number) => {
    setEditingIndex(index)
    setEditingValue(items[index] ?? '')
  }

  const saveEdit = () => {
    if (editingIndex === null) return

    const next = [...items]
    const nextValue = trimValues ? editingValue.trim() : editingValue

    if (!allowEmpty && nextValue === '') {
      next.splice(editingIndex, 1)
    } else {
      next[editingIndex] = nextValue
    }

    updateItems(next)
    setEditingIndex(null)
    setEditingValue('')
  }

  const cancelEdit = () => {
    setEditingIndex(null)
    setEditingValue('')
  }

  const removeItem = (index: number) => {
    const next = items.filter((_, i) => i !== index)
    updateItems(next)
  }

  const addItem = () => {
    const nextValue = trimValues ? draft.trim() : draft
    if (!allowEmpty && !nextValue) return

    updateItems([...items, nextValue])
    setDraft('')
  }

  const handleDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addItem()
    setAdding(false)
    }
  }

  const handleBlurDraft = (e: React.FocusEvent<HTMLInputElement>) => {
    addItem()
    setAdding(false)
  }

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      saveEdit()
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  const [adding, setAdding] = useState<boolean>(false)

  return (
    <div className={className}>
      <div className="tw-flex tw-flex-wrap tw-gap-2 tw-rounded-xl">
        {items.map((item, index) => {
          const isEditing = editingIndex === index

          return (
            <div
              key={`${item}-${index}`}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-300 tw-bg-slate-50 tw-px-2.5 tw-py-1.5"
            >
              {isEditing ? (
                <>
                  <input
                    autoFocus
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onKeyDown={handleEditKeyDown}
                    onBlur={saveEdit}
                    className="tw-min-w-[80px] tw-border-none tw-bg-transparent tw-outline-none"
                  />
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={saveEdit}
                    className="tw-text-slate-600 hover:tw-text-slate-900"
                  >
                    <FaIcon icon={faSave} />
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={cancelEdit}
                    className="tw-text-slate-600 hover:tw-text-slate-900"
                  >
                    <FaIcon icon={faCancel} />
                  </button>
                </>
              ) : (
                <>
                  <span className="tw-text-sm">{item}</span>
                  <button
                    type="button"
                    onClick={() => startEdit(index)}
                    aria-label={`Edit ${item}`}
                    className="tw-text-slate-500 hover:tw-text-slate-900"
                  >
                    <FaIcon icon={faPencilSolid} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    aria-label={`Remove ${item}`}
                    className="tw-text-slate-500 hover:tw-text-red-600"
                  >
                    <FaIcon icon={faTimes} />
                  </button>
                </>
              )}
            </div>
          )
        })}

        <div className="tw-inline-flex tw-items-center tw-gap-1.5">
          {adding ? (
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleDraftKeyDown}
              onBlur={handleBlurDraft}
              placeholder={placeholder}
              className={clx(
                'tw-w-[120px] tw-rounded-full tw-border tw-border-dashed tw-border-slate-400 tw-px-2.5 tw-py-1.5 tw-outline-none focus:tw-border-slate-600',
                {
                  'tw-w-[120px]': !draft,
                },
              )}
            />
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="tw-text-slate-600 hover:tw-text-slate-900"
            >
              <FaIcon icon={faPlusSolid} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
